import type { ExerciseDef } from './types'

// ─── 8.1 Tokenization (added): learn BPE merges ──────────────────────────────

export const bpe: ExerciseDef = {
  starter: `// Byte-pair encoding, from scratch. Tokens start as single characters.

// 1. countPairs(tokens): how often each ADJACENT pair occurs. Key a pair as a + "|" + b.
function countPairs(tokens: string[]): Map<string, number> {
  return new Map()
}

// 2. mergePair(tokens, a, b): a new list where every adjacent a, b becomes the single token a + b.
//    Scan left to right; after merging a pair, skip past both.
function mergePair(tokens: string[], a: string, b: string): string[] {
  return tokens
}

// 3. learnMerges(text, k): k rounds of "find the most common pair, merge it everywhere".
//    Return the merges in order (as [a, b] pairs) and the final tokens. (Ties: the first pair found wins.)
function learnMerges(text: string, k: number): { merges: [string, string][]; tokens: string[] } {
  return { merges: [], tokens: [...text] }
}

const text = data.stories.slice(0, 1500)
const { merges, tokens } = learnMerges(text, 40)
console.log("first merges:", merges.slice(0, 10).map(([a, b]) => JSON.stringify(a + b)).join(" "))
console.log(text.length, "characters →", tokens.length, "tokens")
console.log(tokens.slice(0, 30).map(t => JSON.stringify(t)).join(" "))
`,
  solution: `function countPairs(tokens: string[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (let i = 0; i + 1 < tokens.length; i++) {
    const key = tokens[i] + "|" + tokens[i + 1]
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return counts
}

function mergePair(tokens: string[], a: string, b: string): string[] {
  const out: string[] = []
  for (let i = 0; i < tokens.length; i++) {
    if (i + 1 < tokens.length && tokens[i] === a && tokens[i + 1] === b) {
      out.push(a + b)
      i++
    } else out.push(tokens[i])
  }
  return out
}

function learnMerges(text: string, k: number): { merges: [string, string][]; tokens: string[] } {
  let tokens = [...text]
  const merges: [string, string][] = []
  for (let round = 0; round < k; round++) {
    let best = ""
    let bestCount = 0
    for (const [key, n] of countPairs(tokens)) if (n > bestCount) [best, bestCount] = [key, n]
    if (bestCount < 2) break
    const [a, b] = best.split("|")
    merges.push([a, b])
    tokens = mergePair(tokens, a, b)
  }
  return { merges, tokens }
}

const text = data.stories.slice(0, 1500)
const { merges, tokens } = learnMerges(text, 40)
console.log("first merges:", merges.slice(0, 10).map(([a, b]) => JSON.stringify(a + b)).join(" "))
console.log(text.length, "characters →", tokens.length, "tokens")
console.log(tokens.slice(0, 30).map(t => JSON.stringify(t)).join(" "))
`,
  tests: `
test("countPairs counts adjacent pairs", () => expect(countPairs(["a", "b", "a", "b", "c"]).get("a|b"), "count of a|b").toBe(2))
test("mergePair fuses every occurrence", () => expect(mergePair(["a", "b", "c", "a", "b"], "a", "b"), "mergePair").toEqual(["ab", "c", "ab"]))
test("mergePair doesn't overlap: a a a → aa a", () => expect(mergePair(["a", "a", "a"], "a", "a"), "mergePair").toEqual(["aa", "a"]))
test("the first merge on 'abababc' is a + b", () => expect(learnMerges("abababc", 1).merges, "merges").toEqual([["a", "b"]]))
test("merges build on earlier merges", () => expect(learnMerges("abababab", 2).tokens, "tokens").toEqual(["abab", "abab"]))
test("40 merges shrink the stories by over 25%", () => expect(learnMerges(data.stories.slice(0, 1500), 40).tokens.length, "tokens").toBeLessThan(1500 * 0.75))
`,
}

// ─── 8.2 Bigram model (added): longer contexts, sparser tables ────────────────

export const sparsity: ExerciseDef = {
  starter: `// Why not just count longer contexts — the last 3 or 4 letters instead of 1?
random.seed(42)
const names = random.shuffle(data.names)
const train = names.slice(0, 520), held = names.slice(520)

// contexts(names, n): every context of n letters that comes before some letter, over all the names.
// Pad each name with n dots in front and one at the end: n = 2, "emma" → "..emma."
//   contexts are then "..", ".e", "em", "mm", "ma"  (one before each next letter)
function contexts(names: string[], n: number): string[] {
  return []
}

// unseenShare(n): of the contexts in the held-out names, what fraction never appear in the training names?
function unseenShare(n: number): number {
  return 0
}

for (const n of [1, 2, 3, 4]) {
  console.log(\`context of \${n}: table needs \${27 ** n} rows; \${round(100 * unseenShare(n), 1)}% of new contexts were never seen\`)
}
`,
  solution: `random.seed(42)
const names = random.shuffle(data.names)
const train = names.slice(0, 520), held = names.slice(520)

function contexts(names: string[], n: number): string[] {
  const out: string[] = []
  for (const name of names) {
    const s = ".".repeat(n) + name + "."
    for (let i = n; i < s.length; i++) out.push(s.slice(i - n, i))
  }
  return out
}

function unseenShare(n: number): number {
  const seen = new Set(contexts(train, n))
  const test = contexts(held, n)
  return test.filter(c => !seen.has(c)).length / test.length
}

for (const n of [1, 2, 3, 4]) {
  console.log(\`context of \${n}: table needs \${27 ** n} rows; \${round(100 * unseenShare(n), 1)}% of new contexts were never seen\`)
}
`,
  tests: `
test("contexts of 'emma' with n = 2", () => expect(contexts(["emma"], 2), "contexts").toEqual(["..", ".e", "em", "mm", "ma"]))
test("one context per predicted letter", () => expect(contexts(["ab", "c"], 3), "contexts").toHaveLength(5))
test("single letters are (almost) never new", () => expect(unseenShare(1), "unseenShare(1)").toBeLessThan(0.01))
test("longer contexts are new more and more often", () => expect(unseenShare(4) > unseenShare(3) && unseenShare(3) > unseenShare(2), "4 > 3 > 2").toBe(true))
test("with 4 letters of context, over a quarter of new contexts are unseen", () => expect(unseenShare(4), "unseenShare(4)").toBeGreaterThan(0.25))
`,
}

// ─── 8.5 Neural language model (added): count the parameters ─────────────────

export const nlmParams: ExerciseDef = {
  starter: `// A count table for n letters of context needs one row per possible context, 27 numbers per row.
function tableSize(n: number): number {
  return 0
}

// The neural model: an embedding table (vocab × emb), a hidden layer (ctx·emb → hidden, with biases)
// and an output layer (hidden → vocab, with biases). Count every number.
function nlmParams(vocab: number, emb: number, ctx: number, hidden: number): number {
  return 0
}

for (const ctx of [1, 3, 5, 8]) {
  console.log(\`context \${ctx}:  table \${tableSize(ctx).toExponential(1)} numbers   vs   network \${nlmParams(27, 10, ctx, 64)}\`)
}
`,
  solution: `function tableSize(n: number): number {
  return 27 ** n * 27
}

function nlmParams(vocab: number, emb: number, ctx: number, hidden: number): number {
  return vocab * emb + (ctx * emb * hidden + hidden) + (hidden * vocab + vocab)
}

for (const ctx of [1, 3, 5, 8]) {
  console.log(\`context \${ctx}:  table \${tableSize(ctx).toExponential(1)} numbers   vs   network \${nlmParams(27, 10, ctx, 64)}\`)
}
`,
  tests: `
test("a bigram table (1 letter of context) has 27 × 27 numbers", () => expect(tableSize(1), "tableSize(1)").toBe(729))
test("3 letters of context: 27⁴ numbers", () => expect(tableSize(3), "tableSize(3)").toBe(27 ** 4))
test("the course's model has 270 + 1 984 + 1 755 numbers", () => expect(nlmParams(27, 10, 3, 64), "nlmParams(27, 10, 3, 64)").toBe(270 + 1984 + 1755))
test("more context adds a little to the network, but multiplies the table", () => expect(nlmParams(27, 10, 8, 64) - nlmParams(27, 10, 3, 64), "extra network numbers").toBe(5 * 10 * 64))
`,
}

// ─── 8.7 Sampling (added): top-p ──────────────────────────────────────────────

export const topP: ExerciseDef = {
  starter: `// Top-p ("nucleus") sampling: instead of a fixed k, keep the SMALLEST set of most-likely tokens whose
// probabilities add up to at least p. When the model is sure, that's 1 or 2 tokens; when it's unsure, many.
// topP(probs, p): zero out everything outside that set, renormalise, and keep the original order.
function topP(probs: number[], p: number): number[] {
  return probs
}

console.log(topP([0.05, 0.6, 0.25, 0.1], 0.8).map(v => round(v, 3)))   // keeps 0.6 and 0.25
console.log(topP([0.3, 0.25, 0.25, 0.2], 0.9).map(v => round(v, 3)))   // unsure: keeps all four
`,
  solution: `function topP(probs: number[], p: number): number[] {
  const order = range(probs.length).sort((a, b) => probs[b] - probs[a])
  const keep = new Set<number>()
  let total = 0
  for (const i of order) {
    keep.add(i)
    total += probs[i]
    if (total >= p) break
  }
  return probs.map((q, i) => (keep.has(i) ? q / total : 0))
}

console.log(topP([0.05, 0.6, 0.25, 0.1], 0.8).map(v => round(v, 3)))
console.log(topP([0.3, 0.25, 0.25, 0.2], 0.9).map(v => round(v, 3)))
`,
  tests: `
test("a confident distribution keeps 2 tokens", () => expect(topP([0.05, 0.6, 0.25, 0.1], 0.8), "topP").toBeCloseTo([0, 0.6 / 0.85, 0.25 / 0.85, 0]))
test("an unsure distribution keeps all 4", () => expect(topP([0.3, 0.25, 0.25, 0.2], 0.9).filter(v => v > 0), "kept").toHaveLength(4))
test("p = 1 keeps everything", () => expect(topP([0.5, 0.5], 1), "topP(…, 1)").toBeCloseTo([0.5, 0.5]))
test("the result always sums to 1", () => expect(vec.sum(topP([0.1, 0.2, 0.3, 0.4], 0.5)), "sum").toBeCloseTo(1))
`,
}

// ─── 9.7 Scaling (added): compute-optimal sizes ───────────────────────────────

export const chinchilla: ExerciseDef = {
  starter: `// The Chinchilla rule of thumb: for a fixed compute budget C (in FLOPs), train on about
// 20 tokens per parameter. With training cost C ≈ 6·N·D and D = 20·N:
//   C = 6 · N · 20N = 120 N²   →   N = √(C / 120)
// optimal(C): the compute-optimal { N, D }.
function optimal(C: number): { N: number; D: number } {
  return { N: 0, D: 0 }
}

for (const C of [1e20, 1e22, 5.9e23, 1e25]) {
  const { N, D } = optimal(C)
  console.log(\`\${C.toExponential(1)} FLOPs → \${(N / 1e9).toFixed(1)}B parameters on \${(D / 1e9).toFixed(0)}B tokens\`)
}
`,
  solution: `function optimal(C: number): { N: number; D: number } {
  const N = Math.sqrt(C / 120)
  return { N, D: 20 * N }
}

for (const C of [1e20, 1e22, 5.9e23, 1e25]) {
  const { N, D } = optimal(C)
  console.log(\`\${C.toExponential(1)} FLOPs → \${(N / 1e9).toFixed(1)}B parameters on \${(D / 1e9).toFixed(0)}B tokens\`)
}
`,
  tests: `
test("D is 20 × N", () => {
  const __o = optimal(1e22)
  expect(__o.D / __o.N, "tokens per parameter").toBeCloseTo(20)
})
test("the budget is spent exactly: 6·N·D = C", () => {
  const __o = optimal(3e21)
  expect(6 * __o.N * __o.D, "6ND").toBeCloseTo(3e21, 1e9)
})
test("Chinchilla's budget gives about 70B parameters and 1.4T tokens", () => {
  const __o = optimal(5.9e23)
  expect(__o.N, "N").toBeBetween(6.5e10, 7.5e10)
  expect(__o.D, "D").toBeBetween(1.3e12, 1.5e12)
})
test("100× more compute → only 10× bigger model", () => expect(optimal(1e24).N / optimal(1e22).N, "ratio").toBeCloseTo(10))
`,
}

// ─── 10.5 Diffusion (added): from noise back to data ─────────────────────────

export const denoise: ExerciseDef = {
  starter: `// Generating runs the noising backwards. The network looks at a noisy x_t and predicts the noise ε̂ in it.
// From that guess, you can estimate the clean data, then step to a slightly less noisy x.

// 1. predictX0(xt, epsHat, abar): undo  x_t = √ᾱ·x₀ + √(1 − ᾱ)·ε  for x₀, using the predicted noise.
function predictX0(xt: number[], epsHat: number[], abar: number): number[] {
  return xt
}

// 2. step(xt, epsHat, abarNow, abarPrev): one deterministic step back (this is "DDIM"):
//    estimate x₀, then re-noise it to the LOWER noise level:  √ᾱ_prev·x̂₀ + √(1 − ᾱ_prev)·ε̂
function step(xt: number[], epsHat: number[], abarNow: number, abarPrev: number): number[] {
  return xt
}

// With a perfect noise predictor, stepping back lands exactly on the true, less noisy point:
const x0 = [1.5, -0.5], eps = [0.3, 0.8]
const at = (abar: number) => x0.map((x, i) => Math.sqrt(abar) * x + Math.sqrt(1 - abar) * eps[i])
console.log(step(at(0.2), eps, 0.2, 0.6), "should equal", at(0.6))
`,
  solution: `function predictX0(xt: number[], epsHat: number[], abar: number): number[] {
  return xt.map((x, i) => (x - Math.sqrt(1 - abar) * epsHat[i]) / Math.sqrt(abar))
}

function step(xt: number[], epsHat: number[], abarNow: number, abarPrev: number): number[] {
  const x0hat = predictX0(xt, epsHat, abarNow)
  return x0hat.map((x, i) => Math.sqrt(abarPrev) * x + Math.sqrt(1 - abarPrev) * epsHat[i])
}

const x0 = [1.5, -0.5], eps = [0.3, 0.8]
const at = (abar: number) => x0.map((x, i) => Math.sqrt(abar) * x + Math.sqrt(1 - abar) * eps[i])
console.log(step(at(0.2), eps, 0.2, 0.6), "should equal", at(0.6))
`,
  tests: `
test("predictX0 recovers the clean point when the noise guess is right", () => expect(predictX0([0.6, 0.8], [0, 1], 0.36), "predictX0").toBeCloseTo([1, 0]))
test("one step back lands on the less-noisy point", () => {
  const __x0 = [2, -1], __e = [-0.4, 0.9]
  const __at = a => __x0.map((x, i) => Math.sqrt(a) * x + Math.sqrt(1 - a) * __e[i])
  expect(step(__at(0.1), __e, 0.1, 0.5), "step").toBeCloseTo(__at(0.5))
})
test("stepping all the way to ᾱ = 1 returns the clean data", () => {
  const __x0 = [0.7, 0.2], __e = [1, -1]
  const __xt = __x0.map((x, i) => Math.sqrt(0.05) * x + Math.sqrt(0.95) * __e[i])
  expect(step(__xt, __e, 0.05, 1), "step to ᾱ = 1").toBeCloseTo(__x0)
})
`,
}
